import { useState, useEffect, memo, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Clock, Car, Gift } from 'lucide-react'
import { clsx } from 'clsx'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { formatPrice, formatTimeRemaining } from '@/utils/format'
import type { Listing } from '@/types/listing'

// Module-level cache for seller banner lookups (avoids repeat fetches per session)
const bannerCache = new Map<string, string | null>()

interface ListingCardProps {
  listing: Listing
  showSaveButton?: boolean
  compact?: boolean
  hideBanner?: boolean
  hideStockBadge?: boolean
  className?: string
}

export const ListingCard = memo(function ListingCard({ listing, compact = false, hideBanner = false, hideStockBadge = false, className }: ListingCardProps) {
  const [imgError, setImgError] = useState(false)
  const [bannerUrl, setBannerUrl] = useState<string | null>(bannerCache.get(listing.sellerId) ?? null)

  // Fetch seller banner (cached per session)
  useEffect(() => {
    if (hideBanner || compact || bannerCache.has(listing.sellerId)) return
    let cancelled = false
    getDoc(doc(db, 'users', listing.sellerId)).then((snap) => {
      const url = snap.exists() ? (snap.data().listingBannerUrl as string | undefined) || null : null
      bannerCache.set(listing.sellerId, url)
      if (!cancelled) setBannerUrl(url)
    }).catch(() => {
      bannerCache.set(listing.sellerId, null)
    })
    return () => { cancelled = true }
  }, [listing.sellerId, hideBanner, compact])

  // "New" badge: listing created within last 24 hours
  const isNew = useMemo(() => {
    if (!listing.createdAt) return false
    const created = typeof listing.createdAt.toMillis === 'function'
      ? listing.createdAt.toMillis()
      : typeof listing.createdAt === 'object' && 'seconds' in listing.createdAt
        ? (listing.createdAt as unknown as { seconds: number }).seconds * 1000
        : new Date(listing.createdAt as unknown as string).getTime()
    return Date.now() - created < 24 * 60 * 60 * 1000
  }, [listing.createdAt])

  // Auction urgency badge. Status-aware: a settled/closed auction (ended early
  // or past its time) shows a single neutral "Auction ended" state instead of a
  // live countdown, so the card can't read "Ends today!" and "Closed" at once.
  const auctionUrgency = useMemo(() => {
    if (listing.saleType !== 'auction') return null
    const ended =
      listing.status !== 'active' ||
      (listing as Listing & { auctionEnded?: boolean }).auctionEnded === true
    let msLeft = Infinity
    if (listing.expiresAt) {
      const expires = typeof listing.expiresAt.toMillis === 'function'
        ? listing.expiresAt.toMillis()
        : typeof listing.expiresAt === 'object' && 'seconds' in listing.expiresAt
          ? (listing.expiresAt as unknown as { seconds: number }).seconds * 1000
          : new Date(listing.expiresAt as unknown as string).getTime()
      msLeft = expires - Date.now()
    }
    if (ended || msLeft <= 0) return { label: 'Auction ended', urgent: false, ended: true }
    if (msLeft === Infinity) return null
    if (msLeft < 24 * 60 * 60 * 1000) return { label: 'Ends today!', urgent: true, ended: false }
    const days = Math.ceil(msLeft / (24 * 60 * 60 * 1000))
    return { label: `Ends in ${days}d`, urgent: false, ended: false }
  }, [listing.saleType, listing.expiresAt, listing.status, (listing as Listing & { auctionEnded?: boolean }).auctionEnded])

  const isAuction = listing.saleType === 'auction'
  const isProduct = listing.saleType === 'product'
  const currentPrice = isAuction
    ? (listing as Listing & { currentBid?: number }).currentBid ||
      (listing as Listing & { startingPrice?: number }).startingPrice ||
      listing.price
    : listing.price

  const wasPrice = (listing as Listing & { wasPrice?: number }).wasPrice
  const hasDiscount = wasPrice && listing.price && wasPrice > listing.price

  // Get type badge config
  const getTypeBadge = () => {
    switch (listing.listingType) {
      case 'vehicle':
        return { icon: <Car className="w-3 h-3" />, label: 'Vehicle', className: 'bg-slate-700 text-white' }
      case 'fundraiser':
        return { icon: <Gift className="w-3 h-3" />, label: 'Fundraiser', className: 'bg-primary-500 text-white' }
      default:
        return null
    }
  }

  const typeBadge = getTypeBadge()

  return (
    <Link
      to={`/listing/${listing.id}`}
      className={clsx('group flex flex-col bg-white border border-slate-200 overflow-hidden transition-colors hover:border-primary-500', className)}
      role="article"
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-slate-50">
        {listing.images?.[0] && !imgError ? (
          <img
            src={listing.images[0]}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
            loading="lazy"
            decoding="async"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <span className="text-4xl" aria-label="No image available" role="img">📷</span>
          </div>
        )}

        {/* Badges - Top Left */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
          {isNew && (
            <span className="badge bg-primary-500 text-white text-xs font-semibold animate-bounce-in">New</span>
          )}
          {auctionUrgency && (
            <span className={clsx(
              'badge text-white text-xs font-semibold flex items-center gap-1',
              auctionUrgency.ended ? 'bg-slate-500' : auctionUrgency.urgent ? 'bg-red-500' : 'bg-amber-500'
            )}>
              <Clock className="w-3 h-3" />
              {auctionUrgency.label}
            </span>
          )}
          {/* Hide the plain "Auction" tag once ended — "Auction ended" covers it */}
          {isAuction && !auctionUrgency?.ended && (
            <span className="badge bg-accent-500 text-white text-xs">Auction</span>
          )}
          {isProduct && listing.stock === 0 && !hideStockBadge && (
            <span className="badge bg-red-500 text-white text-xs">Out of Stock</span>
          )}
          {typeBadge && (
            <span className={clsx('badge text-xs flex items-center gap-1', typeBadge.className)}>
              {typeBadge.icon}
              {typeBadge.label}
            </span>
          )}
        </div>

        {/* Auction time remaining — only while genuinely live */}
        {isAuction && listing.expiresAt && !auctionUrgency?.ended && (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTimeRemaining(listing.expiresAt)}
          </div>
        )}
      </div>

      {/* Seller Banner */}
      {!hideBanner && !compact && bannerUrl && (
        <div className="border-t border-slate-100 overflow-hidden h-10 sm:h-16">
          <img
            src={bannerUrl}
            alt="Seller banner"
            className="w-full h-full object-cover object-center"
            loading="lazy"
          />
        </div>
      )}

      {/* Content */}
      <div className={clsx('p-4', compact && 'p-3')}>
        {/* Price */}
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <div className="flex items-baseline gap-2">
            <span className={clsx('font-bold', compact ? 'text-base' : 'text-lg', listing.listingType === 'fundraiser' ? 'text-primary-600' : 'text-slate-900')}>
              {listing.listingType === 'fundraiser' ? 'Fundraiser' : formatPrice(currentPrice)}
            </span>
            {/* Was Price */}
            {!compact && hasDiscount && (
              <span className="text-sm text-slate-400 line-through">
                {formatPrice(wasPrice)}
              </span>
            )}
          </div>
          {!compact && isAuction && (listing as Listing & { bidCount?: number }).bidCount ? (
            <span className="text-xs text-slate-500">
              {(listing as Listing & { bidCount?: number }).bidCount} bids
            </span>
          ) : null}
        </div>

        {/* Title — fixed 2-line height so every card is the same height */}
        <h3 className={clsx(
          'text-slate-700 group-hover:text-primary-600 transition-colors',
          compact ? 'text-sm line-clamp-1 mb-1' : 'text-sm leading-snug line-clamp-2 min-h-[2.5rem]'
        )}>
          {listing.title}
        </h3>

        {/* Fundraiser org name - hide in compact mode */}
        {!compact && listing.listingType === 'fundraiser' && (listing as Listing & { organizationName?: string }).organizationName && (
          <p className="text-xs text-slate-500 mb-2">
            by {(listing as Listing & { organizationName?: string }).organizationName}
          </p>
        )}

        {/* Vehicle specific info - hide in compact mode. truncate keeps it to one
            line so longer make/model strings don't wrap and stretch the card. */}
        {!compact && listing.listingType === 'vehicle' && (
          <div className="text-xs text-slate-500 mb-2 truncate">
            {(listing as Listing & { year?: number; make?: string }).year}{' '}
            {(listing as Listing & { make?: string }).make}{' '}
            {(listing as Listing & { odometer?: number }).odometer && (
              <span className="text-slate-400">
                • {((listing as Listing & { odometer?: number }).odometer || 0).toLocaleString()} km
              </span>
            )}
          </div>
        )}

      </div>
    </Link>
  )
})
